// wishlist.js - Enhanced with localStorage support

class Wishlist {
  constructor() {
    // --- Configuration ---
    this.supabaseUrl = "https://penkozrfaxruujxpotdn.supabase.co";
    this.supabaseKey =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBlbmtvenJmYXhydXVqeHBvdGRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM5NDIyNzYsImV4cCI6MjA2OTUxODI3Nn0.peEO1604jU2dxZD2ZQ1OYLUsxs8sNmK-7MIUQZ8E7Rw";
    // -------------------

    this.buttons = document.querySelectorAll(".wishlist-btn");
    if (this.buttons.length === 0) return;

    // A Set provides fast lookups to check if an item is wishlisted
    this.wishlistedItems = new Set();
    this.customerId = this.buttons[0].dataset.customerId;
    this.isLoggedIn = !!this.customerId;
    this.localStorageKey = "shopify_wishlist";

    // Initialize Supabase client only if user is logged in
    if (this.isLoggedIn && typeof supabase !== "undefined") {
      this.sbClient = supabase.createClient(this.supabaseUrl, this.supabaseKey);
    }

    this.init();
  }

  async init() {
    if (this.isLoggedIn) {
      // Logged in user: sync localStorage with database first, then fetch from database
      await this.syncLocalStorageWithDatabase();
      await this.fetchWishlistFromDatabase();
    } else {
      // Not logged in: load from localStorage
      this.loadWishlistFromLocalStorage();
    }

    this.attachEventListeners();
    this.updateAllButtonStates();

    // Listen for customer login events (when user logs in without page refresh)
    this.setupLoginListener();
  }

  setupLoginListener() {
    // Listen for Shopify customer login event or custom login event
    document.addEventListener("customer:logged_in", async (event) => {
      await this.handleUserLogin(event.detail.customerId);
    });

    // Also check periodically if login state changed (fallback method)
    this.checkLoginStateInterval = setInterval(() => {
      this.checkIfUserLoggedIn();
    }, 2000);
  }

  async checkIfUserLoggedIn() {
    // Re-query for customer ID from buttons
    const buttons = document.querySelectorAll(".wishlist-btn");
    if (buttons.length === 0) return;

    const newCustomerId = buttons[0].dataset.customerId;

    // If we now have a customer ID and didn't before, user just logged in
    if (newCustomerId && !this.isLoggedIn) {
      clearInterval(this.checkLoginStateInterval);
      await this.handleUserLogin(newCustomerId);
    }
  }

  async handleUserLogin(customerId) {
    console.log("User logged in, syncing wishlist...");

    this.customerId = customerId;
    this.isLoggedIn = true;

    // Initialize Supabase client
    if (typeof supabase !== "undefined") {
      this.sbClient = supabase.createClient(this.supabaseUrl, this.supabaseKey);
    }

    // Sync localStorage to database and then fetch from database
    await this.syncLocalStorageWithDatabase();
    await this.fetchWishlistFromDatabase();

    // Update UI to reflect synced state
    this.updateAllButtonStates();
  }

  // === LOCAL STORAGE METHODS ===
  loadWishlistFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const wishlistArray = JSON.parse(stored);
        this.wishlistedItems = new Set(wishlistArray);
      }
    } catch (error) {
      console.error("Error loading wishlist from localStorage:", error);
      this.wishlistedItems = new Set();
    }
  }

  saveWishlistToLocalStorage() {
    try {
      const wishlistArray = Array.from(this.wishlistedItems);
      localStorage.setItem(this.localStorageKey, JSON.stringify(wishlistArray));
    } catch (error) {
      console.error("Error saving wishlist to localStorage:", error);
    }
  }

  clearLocalStorage() {
    try {
      localStorage.removeItem(this.localStorageKey);
    } catch (error) {
      console.error("Error clearing wishlist localStorage:", error);
    }
  }

  // === DATABASE METHODS (for logged-in users) ===
  async fetchWishlistFromDatabase() {
    if (!this.sbClient) return;

    const { data, error } = await this.sbClient
      .from("wishlist_items")
      .select("product_handle")
      .eq("customer_id", this.customerId);

    if (error) {
      console.error("Error fetching wishlist:", error);
      return;
    }

    // Store the handles in the Set for quick checks
    data.forEach((item) => this.wishlistedItems.add(item.product_handle));
  }

  async syncLocalStorageWithDatabase() {
    if (!this.sbClient) return;

    try {
      // Load items from localStorage
      const stored = localStorage.getItem(this.localStorageKey);
      if (!stored) return; // No localStorage data to sync

      const localWishlistItems = JSON.parse(stored);
      if (localWishlistItems.length === 0) return;

      // Get current database wishlist
      const { data: existingItems, error: fetchError } = await this.sbClient
        .from("wishlist_items")
        .select("product_handle")
        .eq("customer_id", this.customerId);

      if (fetchError) {
        console.error("Error fetching existing wishlist for sync:", fetchError);
        return;
      }

      const existingHandles = new Set(
        existingItems.map((item) => item.product_handle)
      );

      // Find items that are in localStorage but not in database
      const itemsToAdd = localWishlistItems.filter(
        (handle) => !existingHandles.has(handle)
      );

      // Add missing items to database
      if (itemsToAdd.length > 0) {
        const itemsToInsert = itemsToAdd.map((handle) => ({
          customer_id: this.customerId,
          product_handle: handle,
        }));

        const { error: insertError } = await this.sbClient
          .from("wishlist_items")
          .insert(itemsToInsert);

        if (insertError) {
          console.error("Error syncing wishlist to database:", insertError);
        } else {
          console.log(
            `Synced ${itemsToAdd.length} items from localStorage to database`
          );
        }
      }

      // Clear localStorage after successful sync
      this.clearLocalStorage();
    } catch (error) {
      console.error("Error during wishlist sync:", error);
    }
  }

  // === UI UPDATE METHODS ===
  updateAllButtonStates() {
    this.buttons = document.querySelectorAll(".wishlist-btn");
    this.buttons.forEach((button) => {
      const productHandle = button.dataset.productHandle;
      const isWishlisted = this.wishlistedItems.has(productHandle);
      this.setButtonState(button, isWishlisted);
    });
  }

  attachEventListeners() {
    this.buttons = document.querySelectorAll(".wishlist-btn");
    this.buttons.forEach((button) => {
      button.addEventListener("click", (e) => this.handleWishlistToggle(e));
    });
  }

  // === WISHLIST TOGGLE METHODS ===
  async handleWishlistToggle(event) {
    const button = event.currentTarget;
    const productHandle = button.dataset.productHandle;
    const isWishlisted = this.wishlistedItems.has(productHandle);

    this.setButtonState(button, isWishlisted, true); // Show loading state

    if (isWishlisted) {
      await this.removeFromWishlist(productHandle, button);
    } else {
      await this.addToWishlist(productHandle, button);
    }
  }

  async addToWishlist(productHandle, button) {
    if (this.isLoggedIn) {
      // Logged in: add to database
      const { error } = await this.sbClient.from("wishlist_items").insert({
        customer_id: this.customerId,
        product_handle: productHandle,
      });

      if (error) {
        console.error("Error adding to wishlist:", error);
        this.setButtonState(button, false); // Revert to unfilled on error
        return;
      }
    }

    // Add to local set and localStorage (for both logged in and out users)
    this.wishlistedItems.add(productHandle);
    this.saveWishlistToLocalStorage();
    this.setButtonState(button, true);
  }

  async removeFromWishlist(productHandle, button) {
    if (this.isLoggedIn) {
      // Logged in: remove from database
      const { error } = await this.sbClient
        .from("wishlist_items")
        .delete()
        .match({
          customer_id: this.customerId,
          product_handle: productHandle,
        });

      if (error) {
        console.error("Error removing from wishlist:", error);
        this.setButtonState(button, true); // Revert to filled on error
        return;
      }
    }

    // Remove from local set and update localStorage
    this.wishlistedItems.delete(productHandle);
    this.saveWishlistToLocalStorage();
    this.setButtonState(button, false);
  }

  setButtonState(button, isWishlisted, isLoading = false) {
    const unfilledIcon = button.querySelector(".wishlist-btn__icon--unfilled");
    const filledIcon = button.querySelector(".wishlist-btn__icon--filled");
    const loadingIcon = button.querySelector(".wishlist-btn__icon--loading");

    unfilledIcon.style.display = "none";
    filledIcon.style.display = "none";
    loadingIcon.style.display = "none";

    if (isLoading) {
      loadingIcon.style.display = "block";
    } else if (isWishlisted) {
      filledIcon.style.display = "block";
    } else {
      unfilledIcon.style.display = "block";
    }
  }

  // === PUBLIC METHODS FOR EXTERNAL USE ===

  // Get wishlist items count (useful for displaying in UI)
  getWishlistCount() {
    return this.wishlistedItems.size;
  }

  // Check if specific product is wishlisted
  isProductWishlisted(productHandle) {
    return this.wishlistedItems.has(productHandle);
  }

  // Get all wishlisted product handles
  getWishlistItems() {
    return Array.from(this.wishlistedItems);
  }
}

// Initialize the Wishlist functionality when the page loads
document.addEventListener("DOMContentLoaded", () => {
  window.__wishlistInstance = new Wishlist();
});

// Expose a reinit helper so dynamic content (like quick view) can refresh listeners
window.reinitWishlistButtons = function reinitWishlistButtons() {
  if (window.__wishlistInstance) {
    window.__wishlistInstance.updateAllButtonStates();
    window.__wishlistInstance.attachEventListeners();
  }
};

// Expose wishlist utilities for external use
window.getWishlistCount = function () {
  return window.__wishlistInstance
    ? window.__wishlistInstance.getWishlistCount()
    : 0;
};

window.isProductWishlisted = function (productHandle) {
  return window.__wishlistInstance
    ? window.__wishlistInstance.isProductWishlisted(productHandle)
    : false;
};

window.getWishlistItems = function () {
  return window.__wishlistInstance
    ? window.__wishlistInstance.getWishlistItems()
    : [];
};

// Expose method to trigger sync when user logs in
window.syncWishlistOnLogin = async function (customerId) {
  if (window.__wishlistInstance) {
    await window.__wishlistInstance.handleUserLogin(customerId);
  }
};
